import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Input,
    Badge,
    UncontrolledTooltip,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import {
    FaTruck,
    FaBox,
    FaCheck,
    FaUser,
    FaMapMarkerAlt,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useDataFetch } from '../../Hooks/Use-data-fetch';
import { InterrapidisimoOfficeModal } from './InterrapidisimoOfficeModal';

const CARRIERS = {
    servientrega: {
        key: 'servientrega',
        label: 'Servientrega',
        short: 'Servi',
        color: '#f59e0b',
        bg: '#fef3c7',
        text: '#92400e',
        solid: '#f59e0b',
    },
    interrapidisimo: {
        key: 'interrapidisimo',
        label: 'Interrapidísimo',
        short: 'Inter',
        color: '#3b82f6',
        bg: '#dbeafe',
        text: '#1e40af',
        solid: '#3b82f6',
    },
};

const PAGE_SIZE = 50;

const PendingShipmentModal = ({ isOpen, toggle }) => {
    const { getData, postData, invalidateQuery } = useDataFetch();
    // id -> total, para conservar selección y monto al cambiar de página
    const [selected, setSelected] = useState({});
    const [activeTab, setActiveTab] = useState('all');
    const [page, setPage] = useState(1);

    const { data, isLoading, isFetching, refetch } = getData(
        ['pending-shipment-orders', activeTab, page],
        '/order/paid/servientrega',
        {
            enabled: isOpen,
            staleTime: 30000,
            placeholderData: (prev) => prev,
        },
        {
            page,
            limit: PAGE_SIZE,
            carrier: activeTab === 'all' ? undefined : activeTab,
        }
    );

    // [{ orderId, reason }] que Inter sólo admite con "reclamo en oficina"
    const [officeOrders, setOfficeOrders] = useState([]);

    const selectedOrders = useMemo(() => Object.keys(selected), [selected]);
    const selectedTotal = useMemo(
        () => Object.values(selected).reduce((sum, t) => sum + (t || 0), 0),
        [selected]
    );

    const { mutate: handleShipOrders, isPending: isShipping } = postData(
        '/order/ship',
        {},
        {
            onSuccess: (response) => {
                // Zonas de difícil acceso de Inter: se juntan para elegir
                // oficina por orden en un solo modal al final.
                const allErrors = response?.data?.errors || [];
                const needOffice = allErrors.filter((err) => err.code === 'INTERRAPIDISIMO_OFFICE_REQUIRED');
                const otherErrors = allErrors.filter((err) => err.code !== 'INTERRAPIDISIMO_OFFICE_REQUIRED');
                setOfficeOrders(needOffice.map((err) => ({ orderId: err.orderId, reason: err.error })));

                if (needOffice.length) {
                    if (response.data.successful?.length) {
                        setSelected({});
                        invalidateQuery(['pending-shipment-orders']);
                    }
                    if (otherErrors.length === 0) return;
                }

                if (otherErrors.length > 0) {
                    const errorMessages = otherErrors
                        .map((err) => `Orden ${err.orderId?.slice(-8)}: ${err.error}`)
                        .join('\n');

                    Swal.fire({
                        icon: 'warning',
                        title: '⚠️ Procesamiento parcial',
                        html: `
                            <div style="text-align: left;">
                                <p><strong>${otherErrors.length} órdenes no se procesaron</strong></p>
                                <div style="font-size: 13px; color: #6c757d; max-height: 150px; overflow-y: auto;">
                                    ${errorMessages}
                                </div>
                                ${
                                    response.data.successful && response.data.successful.length > 0
                                        ? `<hr><p class="text-success">✅ ${response.data.successful.length} órdenes enviadas exitosamente</p>`
                                        : ''
                                }
                            </div>
                        `,
                        timer: 5000,
                        showConfirmButton: true,
                        confirmButtonColor: '#0d6efd',
                        confirmButtonText: 'Entendido',
                    });

                    if (response.data.successful && response.data.successful.length > 0) {
                        setSelected({});
                        invalidateQuery(['pending-shipment-orders']);
                    }
                } else {
                    Swal.fire({
                        icon: 'success',
                        title: '✅ Envíos procesados',
                        text: response?.message || 'Órdenes enviadas exitosamente',
                        timer: 2000,
                        showConfirmButton: false,
                    });

                    setSelected({});
                    invalidateQuery(['pending-shipment-orders']);

                    setTimeout(() => {
                        toggle();
                    }, 2000);
                }
            },
            onError: (error) => {
                Swal.fire({
                    icon: 'error',
                    title: '❌ Error al procesar envíos',
                    text: error.message || 'Ocurrió un error al procesar los envíos',
                    confirmButtonColor: '#dc3545',
                    confirmButtonText: 'Entendido',
                });
            },
        }
    );

    const orders = useMemo(() => data?.data?.orders ?? [], [data]);
    const counts = data?.data?.counts ?? { servientrega: 0, interrapidisimo: 0 };
    const ordersCount = counts.servientrega + counts.interrapidisimo;
    const totalPages = data?.data?.totalPages || 1;
    const tabTotal = data?.data?.total ?? 0;
    const selectedCount = selectedOrders.length;

    // Si al despachar se vacía la última página, retrocede una
    useEffect(() => {
        if (!isFetching && page > 1 && page > totalPages) setPage(totalPages);
    }, [isFetching, page, totalPages]);

    // 🔹 Clasificación directa usando carrier_id
    const getCarrierKey = useCallback((order) => {
        const id = String(order?.carrier_id || '').toLowerCase().trim();
        if (id === 'servientrega') return 'servientrega';
        if (id === 'interrapidisimo') return 'interrapidisimo';
        return null;
    }, []);

    // El backend ya filtra por la pestaña activa
    const filteredOrders = orders;

    const formatAmount = useCallback((amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            maximumFractionDigits: 0,
        }).format(amount || 0);
    }, []);

    const toggleOrder = useCallback((order) => {
        setSelected((prev) => {
            const next = { ...prev };
            if (order.id in next) delete next[order.id];
            else next[order.id] = order.total;
            return next;
        });
    }, []);

    const toggleAll = useCallback(() => {
        const allSelected =
            filteredOrders.length > 0 && filteredOrders.every((o) => o.id in selected);

        setSelected((prev) => {
            const next = { ...prev };
            filteredOrders.forEach((o) => {
                if (allSelected) delete next[o.id];
                else next[o.id] = o.total;
            });
            return next;
        });
    }, [filteredOrders, selected]);

    const changeTab = useCallback((tab) => {
        setActiveTab(tab);
        setPage(1);
    }, []);

    const handleClose = useCallback(() => {
        setSelected({});
        setActiveTab('all');
        setPage(1);
        toggle();
    }, [toggle]);

    const handleConfirmShipment = useCallback(async () => {
        if (selectedCount === 0) {
            Swal.fire({
                icon: 'info',
                title: '⚠️ Selección requerida',
                text: 'Debes seleccionar al menos una orden para enviar',
                confirmButtonColor: '#0d6efd',
                confirmButtonText: 'Entendido',
                timer: 3000,
            });
            return;
        }

        const result = await Swal.fire({
            icon: 'question',
            title: '¿Confirmar envío?',
            html: `
                <div>
                    <p>Estás a punto de enviar <strong>${selectedCount}</strong> orden${selectedCount !== 1 ? 'es' : ''}</p>
                    <p style="font-size: 13px; color: #6c757d;">
                        Total: ${formatAmount(selectedTotal)}
                    </p>
                </div>
            `,
            iconColor: '#f97316',
            showCancelButton: true,
            confirmButtonColor: '#0d6efd',
            cancelButtonColor: '#6c757d',
            confirmButtonText: '✅ Sí, enviar',
            cancelButtonText: '❌ Cancelar',
        });

        if (result.isConfirmed) {
            handleShipOrders({ orderIds: selectedOrders });
        }
    }, [selectedCount, selectedOrders, handleShipOrders, selectedTotal, formatAmount]);

    const getClientName = useCallback((order) => {
        return (
            order.user_id?.name ||
            order.user?.name ||
            order.user_name ||
            order.customer_name ||
            'Sin usuario'
        );
    }, []);

    const getCity = useCallback((order) => {
        return order.shipping_address?.city?.name || 'Sin ciudad';
    }, []);

    const getProducts = useCallback((order) => {
        if (!order.items?.length) return '-';

        const firstProduct = order.items[0]?.product_id?.title || 'Producto';

        if (order.items.length === 1) {
            return `${firstProduct} x${order.items[0].quantity}`;
        }

        return `${firstProduct} + ${order.items.length - 1} producto${
            order.items.length - 1 > 1 ? 's' : ''
        }`;
    }, []);

    const renderCarrierTag = (order) => {
        const key = getCarrierKey(order);
        const cfg = CARRIERS[key];
        if (!cfg) return null;

        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    backgroundColor: cfg.bg,
                    color: cfg.text,
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '5px 9px',
                    borderRadius: 999,
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                }}
            >
                <FaTruck size={9} />
                {cfg.label}
            </span>
        );
    };

    const renderOrderRow = (order) => {
        const isSelected = order.id in selected;
        const carrierKey = getCarrierKey(order);
        const cfg = CARRIERS[carrierKey];

        return (
            <tr
                key={order.id}
                style={{
                    backgroundColor: isSelected ? '#f0f7ff' : '#ffffff',
                    cursor: 'pointer',
                    borderLeft: `3px solid ${cfg?.solid || 'transparent'}`,
                }}
                onClick={() => toggleOrder(order)}
            >
                <td style={{ padding: '12px 15px' }}>
                    <label
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: 0,
                            cursor: 'pointer',
                            position: 'relative',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleOrder(order)}
                            style={{
                                appearance: 'none',
                                WebkitAppearance: 'none',
                                width: 18,
                                height: 18,
                                border: '2px solid #0d6efd',
                                borderRadius: 4,
                                backgroundColor: isSelected ? '#0d6efd' : '#ffffff',
                                cursor: 'pointer',
                                position: 'relative',
                                margin: 0,
                            }}
                        />
                        {isSelected && (
                            <span
                                style={{
                                    position: 'absolute',
                                    color: '#ffffff',
                                    fontSize: 13,
                                    fontWeight: 'bold',
                                    pointerEvents: 'none',
                                }}
                            >
                                ✓
                            </span>
                        )}
                    </label>
                </td>

                <td>
                    <Link
                        id={`oid-${order.id}`}
                        to={`/order/detail/${order.id}`}
                        target="_blank"
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontWeight: 700 }}
                    >
                        #{order.id?.slice(-8)}
                    </Link>
                    <UncontrolledTooltip target={`oid-${order.id}`}>{order.id}</UncontrolledTooltip>
                    {order.invoice_number && (
                        <div style={{ fontSize: 10, color: '#868e96', marginTop: 2 }}>
                            Factura: {order.invoice_number}
                        </div>
                    )}
                </td>

                <td>
                    <div className="d-flex align-items-center">
                        <div
                            className="d-flex align-items-center justify-content-center me-2"
                            style={{
                                width: 30,
                                height: 30,
                                borderRadius: '50%',
                                backgroundColor: '#eef4ff',
                                color: '#0d6efd',
                                flexShrink: 0,
                            }}
                        >
                            <FaUser size={12} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <div
                                style={{
                                    fontWeight: 600,
                                    color: '#343a40',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 160,
                                }}
                            >
                                {getClientName(order)}
                            </div>
                            <div
                                style={{
                                    fontSize: 10,
                                    color: '#868e96',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 160,
                                }}
                            >
                                {order.user_id?.email || '-'}
                            </div>
                        </div>
                    </div>
                </td>

                <td>
                    <div
                        style={{
                            maxWidth: 200,
                            color: '#495057',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {getProducts(order)}
                    </div>
                </td>

                <td>
                    <div className="d-flex align-items-center">
                        <FaMapMarkerAlt className="me-1 text-muted" size={11} />
                        <div>
                            <div style={{ fontWeight: 500 }}>{getCity(order)}</div>
                            <div style={{ fontSize: 10, color: '#868e96' }}>
                                {order.shipping_address?.province?.name || '-'}
                            </div>
                        </div>
                    </div>
                </td>

                <td>
                    <div style={{ color: '#495057', whiteSpace: 'nowrap' }}>
                        {order.created_at
                            ? new Date(order.created_at).toLocaleDateString('es-CO')
                            : '-'}
                    </div>
                    {order.created_at && (
                        <div style={{ fontSize: 10, color: '#adb5bd' }}>
                            {new Date(order.created_at).toLocaleTimeString('es-CO', {
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        </div>
                    )}
                </td>

                <td>
                    <div style={{ fontWeight: 700, color: '#212529', whiteSpace: 'nowrap' }}>
                        {formatAmount(order.total)}
                    </div>
                </td>

                <td>{renderCarrierTag(order)}</td>

                <td>
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            backgroundColor: '#22c55e',
                            color: '#ffffff',
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '6px 10px',
                            borderRadius: 999,
                            lineHeight: 1,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <FaCheck size={9} />
                        Pagada
                    </span>
                </td>
            </tr>
        );
    };

    const TabButton = ({ value, label, count, carrierCfg, icon }) => {
        const isActive = activeTab === value;
        const bg = isActive
            ? carrierCfg?.solid || '#0d6efd'
            : '#ffffff';
        const color = isActive ? '#ffffff' : carrierCfg?.text || '#495057';

        return (
            <button
                type="button"
                onClick={() => changeTab(value)}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 14px',
                    borderRadius: 999,
                    border: `1px solid ${isActive ? bg : '#e9ecef'}`,
                    backgroundColor: bg,
                    color,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                }}
            >
                {icon}
                <span>{label}</span>
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 22,
                        height: 20,
                        padding: '0 7px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        backgroundColor: isActive
                            ? 'rgba(255,255,255,0.25)'
                            : carrierCfg?.bg || '#f1f5f9',
                        color: isActive ? '#ffffff' : carrierCfg?.text || '#475569',
                    }}
                >
                    {count}
                </span>
            </button>
        );
    };

    const allVisibleSelected =
        filteredOrders.length > 0 && filteredOrders.every((o) => o.id in selected);

    return (
        <>
        <Modal isOpen={isOpen} toggle={handleClose} size="xl" centered backdrop="static">
            <ModalHeader
                toggle={handleClose}
                style={{
                    borderBottom: '1px solid #e9ecef',
                    padding: '16px 20px',
                }}
            >
                <div className="d-flex align-items-center w-100">
                    <div
                        className="d-flex align-items-center justify-content-center me-3"
                        style={{
                            width: 38,
                            height: 38,
                            borderRadius: 8,
                            backgroundColor: '#fff7ed',
                            color: '#f97316',
                        }}
                    >
                        <FaTruck size={17} />
                    </div>

                    <div className="flex-grow-1">
                        <div
                            style={{
                                fontSize: 15,
                                fontWeight: 700,
                                color: '#212529',
                            }}
                        >
                            Órdenes pendientes de envío
                        </div>
                        <div
                            style={{
                                fontSize: 11,
                                color: '#6c757d',
                                marginTop: 2,
                            }}
                        >
                            {isLoading
                                ? 'Cargando...'
                                : `${ordersCount} órdenes listas para enviar`}
                        </div>
                    </div>

                    {!isLoading && ordersCount > 0 && (
                        <Button
                            color="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            onClick={refetch}
                            style={{ fontSize: 12 }}
                        >
                            ↻ Actualizar
                        </Button>
                    )}
                </div>
            </ModalHeader>

            <ModalBody
                style={{
                    padding: 20,
                    backgroundColor: '#f8f9fa',
                    maxHeight: '70vh',
                    overflowY: 'auto',
                }}
            >
                {isLoading ? (
                    <div
                        className="d-flex flex-column justify-content-center align-items-center"
                        style={{ minHeight: 250 }}
                    >
                        <Spinner color="primary" style={{ width: 40, height: 40 }} />
                        <div className="mt-3 text-muted" style={{ fontSize: 13 }}>
                            Cargando órdenes...
                        </div>
                    </div>
                ) : ordersCount === 0 ? (
                    <div className="text-center bg-white border rounded p-5">
                        <FaBox
                            size={32}
                            style={{
                                color: '#adb5bd',
                                marginBottom: 12,
                            }}
                        />
                        <div
                            style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: '#495057',
                            }}
                        >
                            No hay órdenes pendientes de envío
                        </div>
                        <div
                            style={{
                                fontSize: 12,
                                color: '#868e96',
                                marginTop: 5,
                            }}
                        >
                            Todas las órdenes pagadas han sido gestionadas.
                        </div>
                    </div>
                ) : (
                    <>
                        {/* 🔹 TAGS / TABS DE CLASIFICACIÓN */}
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                            <TabButton
                                value="all"
                                label="Todas"
                                count={ordersCount}
                                icon={<FaBox size={11} />}
                            />
                            <TabButton
                                value="servientrega"
                                label="Servientrega"
                                count={counts.servientrega}
                                carrierCfg={CARRIERS.servientrega}
                                icon={<FaTruck size={11} />}
                            />
                            <TabButton
                                value="interrapidisimo"
                                label="Interrapidísimo"
                                count={counts.interrapidisimo}
                                carrierCfg={CARRIERS.interrapidisimo}
                                icon={<FaTruck size={11} />}
                            />
                        </div>

                        <div className="bg-white border rounded" style={{ overflow: 'hidden' }}>
                            <div
                                className="d-flex justify-content-between align-items-center"
                                style={{
                                    padding: '12px 15px',
                                    borderBottom: '1px solid #e9ecef',
                                }}
                            >
                                <div>
                                    <span
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: '#495057',
                                        }}
                                    >
                                        {tabTotal} órdenes
                                        {activeTab !== 'all' && (
                                            <span className="text-muted ms-1">
                                                ({CARRIERS[activeTab]?.label})
                                            </span>
                                        )}
                                    </span>

                                    {selectedCount > 0 && (
                                        <span
                                            style={{
                                                fontSize: 11,
                                                color: '#0d6efd',
                                                marginLeft: 10,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {selectedCount} seleccionada
                                            {selectedCount !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                </div>

                                <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 text-decoration-none"
                                    onClick={toggleAll}
                                >
                                    {allVisibleSelected
                                        ? 'Deseleccionar visibles'
                                        : 'Seleccionar visibles'}
                                </Button>
                            </div>

                            <div className="table-responsive">
                                <table
                                    className="table table-hover align-middle mb-0"
                                    style={{ fontSize: 12 }}
                                >
                                    <thead>
                                        <tr
                                            style={{
                                                backgroundColor: '#f8f9fa',
                                                borderBottom: '1px solid #e9ecef',
                                            }}
                                        >
                                            <th style={{ width: 45, padding: '11px 15px' }}>
                                                <Input
                                                    type="checkbox"
                                                    checked={allVisibleSelected}
                                                    onChange={toggleAll}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </th>
                                            <th>ORDEN</th>
                                            <th>CLIENTE</th>
                                            <th>PRODUCTO</th>
                                            <th>DESTINO</th>
                                            <th>FECHA</th>
                                            <th>TOTAL</th>
                                            <th>TRANSPORTADORA</th>
                                            <th>ESTADO</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {filteredOrders.length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan={9}
                                                    className="text-center py-4 text-muted"
                                                >
                                                    No hay órdenes para esta transportadora.
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredOrders.map(renderOrderRow)
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {totalPages > 1 && (
                                <div
                                    className="d-flex justify-content-between align-items-center"
                                    style={{ padding: '10px 15px', borderTop: '1px solid #e9ecef' }}
                                >
                                    <span style={{ fontSize: 12, color: '#6c757d' }}>
                                        Página {page} de {totalPages}
                                        {isFetching && <Spinner size="sm" className="ms-2" />}
                                    </span>
                                    <div className="d-flex gap-2">
                                        <Button
                                            color="secondary"
                                            outline
                                            size="sm"
                                            disabled={page <= 1 || isFetching}
                                            onClick={() => setPage((p) => p - 1)}
                                        >
                                            ‹ Anterior
                                        </Button>
                                        <Button
                                            color="secondary"
                                            outline
                                            size="sm"
                                            disabled={page >= totalPages || isFetching}
                                            onClick={() => setPage((p) => p + 1)}
                                        >
                                            Siguiente ›
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </ModalBody>

            <ModalFooter
                style={{
                    borderTop: '1px solid #e9ecef',
                    padding: '12px 20px',
                }}
            >
                <div className="me-auto">
                    {selectedCount > 0 && (
                        <span
                            style={{
                                fontSize: 12,
                                color: '#6c757d',
                                fontWeight: 500,
                            }}
                        >
                            {selectedCount} orden{selectedCount !== 1 ? 'es' : ''} seleccionada
                            {selectedCount !== 1 ? 's' : ''}
                            <span className="ms-2 text-primary">
                                Total:{' '}
                                {formatAmount(selectedTotal)}
                            </span>
                        </span>
                    )}
                </div>

                <Button
                    color="secondary"
                    outline
                    size="sm"
                    onClick={handleClose}
                    disabled={isShipping}
                >
                    Cerrar
                </Button>

                <Button
                    color="primary"
                    size="sm"
                    disabled={selectedCount === 0 || isShipping}
                    onClick={handleConfirmShipment}
                >
                    {isShipping ? (
                        <>
                            <Spinner size="sm" className="me-1" />
                            Procesando...
                        </>
                    ) : (
                        <>
                            <FaTruck className="me-1" size={12} />
                            Gestionar envío {selectedCount > 0 && `(${selectedCount})`}
                        </>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
        {officeOrders.length > 0 && (
            <InterrapidisimoOfficeModal
                orders={officeOrders}
                toggle={() => setOfficeOrders([])}
                isShipping={isShipping}
                onConfirm={(offices) => handleShipOrders({ orderIds: Object.keys(offices), offices })}
            />
        )}
        </>
    );
};

export default PendingShipmentModal;